import { useCallback, useEffect, useId, useState, type FormEvent } from 'react';
import { BookOpen, Check, Copy, KeyRound, MailCheck, Plus } from 'lucide-react';
import { api, ErroApi } from '../../api/cliente';
import type { ApiKey, ApiKeyCriada } from '../../api/tipos';
import { useSessao } from '../../auth/SessaoContext';
import {
  Aviso,
  Botao,
  BotaoLink,
  Campo,
  Cartao,
  Celula,
  Crescer,
  Definicoes,
  Esqueleto,
  Etiqueta,
  Tabela,
  Td,
  Th,
  useConfirmar,
  useNotificar,
  Vazio,
} from '../../componentes/ui';
import { dataHora, tempoRelativo } from '../../util/formato';
import {
  formatarDocumento,
  mascaraCep,
  mascaraTelefone,
} from '../../util/mascaras';
import { Colunas, Pagina, TituloPagina } from '../../componentes/Pagina';
import css from './Configuracoes.module.css';

export function Configuracoes() {
  return (
    <Pagina>
      <TituloPagina
        titulo="Configurações"
        descricao="Sua conta, sua senha e as chaves de integração."
      />

      <Colunas minimo={400} alinhar="start">
        <DadosDaConta />
        <TrocarSenha />
      </Colunas>

      {/* Lista com ações: ocupa a largura toda. */}
      <ChavesDeApi />
    </Pagina>
  );
}

/* ── Conta ──────────────────────────────────────────────────────────────── */

function DadosDaConta() {
  const { usuario } = useSessao();
  if (!usuario) return null;

  const pj = usuario.tipoPessoa === 'JURIDICA';
  const complemento = usuario.complemento ? `, ${usuario.complemento}` : '';

  return (
    <Cartao
      titulo="Sua conta"
      acoes={
        usuario.emailVerificadoEm ? (
          <Etiqueta tom="ok">E-mail verificado</Etiqueta>
        ) : (
          <BotaoLink
            to="/verificar-email"
            aparencia="secundario"
            pequeno
            icone={<MailCheck />}
          >
            Verificar e-mail
          </BotaoLink>
        )
      }
    >
      <Definicoes
        itens={[
          [pj ? 'Razão social' : 'Nome', usuario.nome],
          ['E-mail', usuario.email],
          ['Tipo', pj ? 'Pessoa jurídica' : 'Pessoa física'],
          [pj ? 'CNPJ' : 'CPF', formatarDocumento(usuario.documento)],
          ...(usuario.telefone
            ? ([['Telefone', mascaraTelefone(usuario.telefone)]] as const)
            : []),
          [
            'Endereço',
            `${usuario.logradouro}, ${usuario.numero}${complemento}`,
          ],
          [
            'Cidade',
            `${usuario.bairro} — ${usuario.cidade}/${usuario.uf} · ${mascaraCep(usuario.cep)}`,
          ],
          ['Papel', usuario.role === 'ADMIN' ? 'Administrador' : 'Usuário'],
          ['Conta criada em', dataHora(usuario.createdAt)],
        ]}
      />
    </Cartao>
  );
}

/* ── Senha ──────────────────────────────────────────────────────────────── */

function TrocarSenha() {
  const idForm = useId();
  const [atual, setAtual] = useState('');
  const [nova, setNova] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [pronto, setPronto] = useState(false);
  const [enviando, setEnviando] = useState(false);

  async function enviar(e: FormEvent) {
    e.preventDefault();
    setErro(null);
    setPronto(false);
    setEnviando(true);
    try {
      await api<void>('/auth/senha', {
        metodo: 'PATCH',
        corpo: { senhaAtual: atual, senhaNova: nova },
      });
      setPronto(true);
      setAtual('');
      setNova('');
      // O servidor revoga todas as sessões e limpa os cookies; a próxima
      // requisição do painel vai receber 401 e cair no login. Avisamos antes
      // para não parecer um erro aleatório.
    } catch (err) {
      setErro(
        err instanceof ErroApi
          ? err.message
          : 'Não foi possível trocar a senha.',
      );
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Cartao
      titulo="Trocar senha"
      descricao="Trocar a senha encerra todas as sessões abertas, inclusive esta."
      rodape={
        <Botao
          type="submit"
          form={idForm}
          carregando={enviando}
          disabled={!atual || !nova}
        >
          Salvar nova senha
        </Botao>
      }
    >
      <form id={idForm} onSubmit={enviar} className={css.formulario}>
        {erro && <Aviso>{erro}</Aviso>}
        {pronto && (
          <Aviso tom="ok">
            Senha alterada. Todas as sessões foram encerradas — entre de novo
            com a senha nova.
          </Aviso>
        )}

        <Campo
          rotulo="Senha atual"
          type="password"
          autoComplete="current-password"
          required
          value={atual}
          onChange={(e) => setAtual(e.target.value)}
        />
        <Campo
          rotulo="Nova senha"
          type="password"
          autoComplete="new-password"
          required
          value={nova}
          onChange={(e) => setNova(e.target.value)}
          dica="Mínimo de 10 caracteres, com uma letra e um número."
        />
      </form>
    </Cartao>
  );
}

/* ── API keys ───────────────────────────────────────────────────────────── */

function ChavesDeApi() {
  const confirmar = useConfirmar();
  const notificar = useNotificar();
  const [chaves, setChaves] = useState<ApiKey[] | null>(null);
  const [nome, setNome] = useState('');
  const [criada, setCriada] = useState<ApiKeyCriada | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [copiou, setCopiou] = useState(false);

  const carregar = useCallback(async () => {
    try {
      setChaves(await api<ApiKey[]>('/auth/api-keys'));
    } catch (err) {
      setErro(
        err instanceof ErroApi ? err.message : 'Falha ao carregar as chaves.',
      );
    }
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  async function criar(e: FormEvent) {
    e.preventDefault();
    setErro(null);
    setEnviando(true);
    try {
      const nova = await api<ApiKeyCriada>('/auth/api-keys', {
        metodo: 'POST',
        corpo: { nome },
      });
      setCriada(nova);
      setNome('');
      await carregar();
    } catch (err) {
      setErro(
        err instanceof ErroApi
          ? err.message
          : 'Não foi possível criar a chave.',
      );
    } finally {
      setEnviando(false);
    }
  }

  async function revogar(chave: ApiKey) {
    const ok = await confirmar({
      titulo: 'Revogar esta chave?',
      mensagem: `Qualquer integração usando "${chave.nome}" para de funcionar na hora. Não dá para desfazer.`,
      confirmar: 'Revogar chave',
      cancelar: 'Voltar',
      perigo: true,
    });
    if (!ok) return;
    setErro(null);
    try {
      await api<ApiKey>(`/auth/api-keys/${chave.id}`, { metodo: 'DELETE' });
      notificar('Chave revogada.');
      await carregar();
    } catch (err) {
      setErro(
        err instanceof ErroApi ? err.message : 'Não foi possível revogar.',
      );
    }
  }

  async function copiar(texto: string) {
    try {
      await navigator.clipboard.writeText(texto);
      setCopiou(true);
      window.setTimeout(() => setCopiou(false), 2000);
    } catch {
      // Sem permissão de clipboard (http sem localhost, por exemplo): o valor
      // segue visível na tela para copiar à mão.
      setErro(
        'Não foi possível copiar automaticamente. Selecione e copie o texto.',
      );
    }
  }

  return (
    <Cartao
      padding={0}
      titulo="Chaves de API"
      descricao={
        <>
          Para integrar seu sistema direto com a API, sem passar por este
          painel: consulta por chave, lotes, notas da captura e webhooks. Envie
          no header <code>x-api-key</code>. O painel não usa chave: a sessão
          dele fica em cookie que o navegador não entrega a scripts.
        </>
      }
      acoes={
        <BotaoLink
          to="/desenvolvedores"
          aparencia="secundario"
          pequeno
          icone={<BookOpen />}
        >
          Documentação da API
        </BotaoLink>
      }
    >
      <form onSubmit={criar} className={css.novaChave}>
        <Crescer>
          <Campo
            rotulo="Nome da chave"
            required
            maxLength={80}
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            placeholder="Ex.: servidor de produção"
          />
        </Crescer>
        <Botao
          type="submit"
          aparencia="secundario"
          icone={<Plus />}
          carregando={enviando}
          disabled={!nome.trim()}
        >
          Gerar chave
        </Botao>
        <p className={css.dica}>
          O nome é só para você identificar depois onde a chave está sendo
          usada.
        </p>
      </form>

      {(erro || criada) && (
        <div className={css.avisos}>
          {erro && <Aviso>{erro}</Aviso>}
          {criada && (
            <Aviso
              tom="ok"
              titulo="Copie agora — este segredo não será mostrado de novo."
            >
              <div className={css.segredo}>
                <code className={css.token}>{criada.token}</code>
                <div className={css.botoes}>
                  <Botao
                    pequeno
                    icone={copiou ? <Check /> : <Copy />}
                    onClick={() => void copiar(criada.token)}
                  >
                    {copiou ? 'Copiado' : 'Copiar'}
                  </Botao>
                  <Botao
                    aparencia="secundario"
                    pequeno
                    onClick={() => setCriada(null)}
                  >
                    Já guardei
                  </Botao>
                </div>
              </div>
            </Aviso>
          )}
        </div>
      )}

      {chaves === null ? (
        <div className={css.carregando}>
          <Esqueleto altura={20} />
          <Esqueleto altura={20} />
        </div>
      ) : chaves.length === 0 ? (
        <Vazio
          icone={<KeyRound />}
          titulo="Nenhuma chave criada"
          descricao="Crie uma chave se for chamar a API a partir de outro sistema."
        />
      ) : (
        <Tabela rotulo="Chaves de API">
          <thead>
            <tr>
              <Th>Chave</Th>
              <Th>Criada em</Th>
              <Th>Último uso</Th>
              <Th>Situação</Th>
              <Th encolher>
                <span className="sr-only">Ações</span>
              </Th>
            </tr>
          </thead>
          <tbody>
            {chaves.map((c) => {
              const revogada = c.revogadaEm !== null;
              const expirada =
                !revogada &&
                c.expiraEm !== null &&
                new Date(c.expiraEm) <= new Date();
              return (
                <tr
                  key={c.id}
                  className={revogada || expirada ? css.inativa : undefined}
                >
                  <Td>
                    <Celula
                      principal={c.nome}
                      secundaria={
                        <code className={css.prefixo}>bxk_{c.id}_…</code>
                      }
                    />
                  </Td>
                  <Td>
                    <span className={css.data}>{dataHora(c.createdAt)}</span>
                  </Td>
                  <Td>
                    <span
                      className={css.data}
                      title={c.ultimoUso ? dataHora(c.ultimoUso) : undefined}
                    >
                      {c.ultimoUso ? tempoRelativo(c.ultimoUso) : 'Nunca usada'}
                    </span>
                  </Td>
                  <Td>
                    {revogada ? (
                      <Etiqueta tom="erro">Revogada</Etiqueta>
                    ) : expirada ? (
                      <Etiqueta tom="alerta">Expirada</Etiqueta>
                    ) : (
                      <Etiqueta tom="ok">Ativa</Etiqueta>
                    )}
                  </Td>
                  <Td encolher>
                    {!revogada && !expirada && (
                      <Botao
                        aparencia="perigo"
                        pequeno
                        onClick={() => void revogar(c)}
                      >
                        Revogar
                      </Botao>
                    )}
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </Tabela>
      )}
    </Cartao>
  );
}
